import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle2, ChevronRight, CircleAlert, Stamp, TriangleAlert } from 'lucide-react';
import { Page, StatTile } from '../../components/layout/Page.js';
import { Card, CardBody, CardHeader, CardTitle, Code, EmptyState, Skeleton } from '../../components/ui/primitives.js';
import { Button } from '../../components/ui/button.js';
import { useMoulds, type MouldRow } from '../../db/queries.js';
import { RULES, type Severity } from './rules.js';
import { cn } from '../../lib/cn.js';

/**
 * The data-quality report - a Week 1 deliverable, not a nice-to-have.
 *
 * The client's source spreadsheet has 67 moulds of which 19 carry no owner and
 * 66 no location. Everything downstream (nameplate engraving, ownership
 * reports, location tracking) inherits those gaps, so the gaps have to be
 * visible and closeable *before* week 2 cuts any metal.
 *
 * Severity is about consequence, not tidiness:
 *   - `blocker`  - cannot engrave a correct nameplate without it
 *   - `warning`  - reports will be wrong or incomplete
 *   - `info`     - worth filling in eventually
 */

const SEVERITY_META: Record<Severity, { label: string; tone: string; icon: typeof CircleAlert }> = {
  blocker: {
    label: 'Engraved on the nameplate',
    tone: 'text-danger border-danger/30 bg-danger-subtle',
    icon: CircleAlert,
  },
  warning: {
    label: 'Affects reports',
    tone: 'text-warning border-warning/30 bg-warning-subtle',
    icon: TriangleAlert,
  },
  info: { label: 'Nice to have', tone: 'text-info border-info/30 bg-info-subtle', icon: CircleAlert },
};

export function DataQualityPage() {
  const moulds = useMoulds({});
  const [openRule, setOpenRule] = useState<string | null>(null);

  const report = useMemo(() => {
    if (!moulds) return null;

    const failures = new Map<string, MouldRow[]>();
    for (const rule of RULES) failures.set(rule.id, []);
    for (const mould of moulds) {
      for (const rule of RULES) {
        if (rule.test(mould)) failures.get(rule.id)!.push(mould);
      }
    }

    // Duplicate codes are a separate shape of problem: they break the QR payload
    // contract, where one code must mean exactly one mould.
    //
    // A mould can legitimately have no code yet. The form invites exactly that -
    // leave the Mould ID blank and the server allocates one from the project - so
    // between saving and the next sync the local row has none. Assuming otherwise
    // here threw on `.trim()` and took the whole page down with it.
    const byCode = new Map<string, MouldRow[]>();
    for (const m of moulds) {
      const key = m.assetCode?.trim().toUpperCase();
      if (!key) continue; // not yet allocated: cannot duplicate what does not exist
      byCode.set(key, [...(byCode.get(key) ?? []), m]);
    }
    const duplicates = [...byCode.values()].filter((g) => g.length > 1);

    const blockerIds = RULES.filter((r) => r.severity === 'blocker').map((r) => r.id);
    const readyToEngrave = moulds.filter((m) =>
      blockerIds.every((id) => !failures.get(id)!.some((f) => f.id === m.id)),
    ).length;

    const totalCells = moulds.length * RULES.length;
    const filledCells = totalCells - RULES.reduce((sum, r) => sum + failures.get(r.id)!.length, 0);

    return {
      failures,
      duplicates,
      readyToEngrave,
      completeness: totalCells ? Math.round((filledCells / totalCells) * 100) : 100,
      total: moulds.length,
    };
  }, [moulds]);

  if (!report) {
    return (
      <Page title="Data Quality">
        <div className="grid gap-3 sm:grid-cols-3">
          {[0, 1, 2].map((i) => (
            <Skeleton key={i} className="h-20" />
          ))}
        </div>
      </Page>
    );
  }

  const bySeverity = (s: Severity) =>
    RULES.filter((r) => r.severity === s && report.failures.get(r.id)!.length > 0);

  const allClean = RULES.every((r) => report.failures.get(r.id)!.length === 0) && !report.duplicates.length;

  return (
    <Page
      title="Data Quality"
      description="What is missing from the catalog, and what it blocks."
    >
      <div className="mb-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <StatTile label="Moulds in catalog" value={report.total} />
        <StatTile
          label="Complete nameplates"
          value={report.readyToEngrave}
          hint={`${report.total - report.readyToEngrave} would have some details engraved blank - every mould can still be engraved`}
          tone={report.readyToEngrave < report.total ? 'warning' : 'default'}
          icon={<Stamp />}
        />
        <StatTile label="Field completeness" value={`${report.completeness}%`} />
        <StatTile
          label="Duplicate mould IDs"
          value={report.duplicates.length}
          tone={report.duplicates.length ? 'danger' : 'default'}
          hint={report.duplicates.length ? 'One ID must mean one mould' : 'All IDs unique'}
        />
      </div>

      {allClean ? (
        <Card>
          <EmptyState
            icon={<CheckCircle2 className="text-success" />}
            title="Nothing outstanding"
            description="Every mould has all its catalog and nameplate fields. This is the state to reach before week 2."
          />
        </Card>
      ) : null}

      {report.duplicates.length ? (
        <Card className="mb-4 border-danger/30">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-danger">
              <CircleAlert className="size-4" aria-hidden />
              Duplicate mould IDs
            </CardTitle>
          </CardHeader>
          <CardBody className="space-y-2">
            <p className="text-sm text-muted-foreground">
              A QR code encodes the mould ID, so two moulds sharing one ID cannot be told apart by a
              scan. Fill these in to have them engraved; a plate made now leaves them off.
            </p>
            {report.duplicates.map((group) => (
              <div key={group[0]?.assetCode} className="flex flex-wrap items-center gap-2 rounded-lg bg-danger-subtle px-3 py-2">
                <Code className="font-semibold">{group[0]?.assetCode}</Code>
                <span className="text-xs text-muted-foreground">used by</span>
                {group.map((m) => (
                  <Link
                    key={m.id}
                    to={`/moulds/${encodeURIComponent(m.assetCode)}`}
                    className="text-xs font-medium underline underline-offset-2"
                  >
                    {m.name}
                  </Link>
                ))}
              </div>
            ))}
          </CardBody>
        </Card>
      ) : null}

      <div className="space-y-4">
        {(['blocker', 'warning', 'info'] as Severity[]).map((severity) => {
          const rules = bySeverity(severity);
          if (!rules.length) return null;
          const meta = SEVERITY_META[severity];
          const Icon = meta.icon;

          return (
            <Card key={severity}>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Icon
                    className={cn(
                      'size-4',
                      severity === 'blocker' && 'text-danger',
                      severity === 'warning' && 'text-warning',
                      severity === 'info' && 'text-info',
                    )}
                    aria-hidden
                  />
                  {meta.label}
                </CardTitle>
                <span className="text-xs text-muted-foreground">
                  {rules.length} issue{rules.length === 1 ? '' : 's'}
                </span>
              </CardHeader>
              <ul className="divide-y divide-border">
                {rules.map((rule) => {
                  const rows = report.failures.get(rule.id)!;
                  const isOpen = openRule === rule.id;
                  return (
                    <li key={rule.id}>
                      <button
                        type="button"
                        onClick={() => setOpenRule(isOpen ? null : rule.id)}
                        aria-expanded={isOpen}
                        className="flex w-full items-center gap-3 px-4 py-3 text-left hover:bg-surface-sunken"
                      >
                        <ChevronRight
                          className={cn(
                            'size-4 shrink-0 text-muted-foreground transition-transform',
                            isOpen && 'rotate-90',
                          )}
                          aria-hidden
                        />
                        <span className="min-w-0 flex-1">
                          <span className="block text-sm font-medium">{rule.label}</span>
                          <span className="block text-xs text-muted-foreground">{rule.why}</span>
                        </span>
                        <span
                          className={cn(
                            'shrink-0 rounded-md border px-2 py-0.5 text-xs font-semibold tabular',
                            meta.tone,
                          )}
                        >
                          {rows.length}
                        </span>
                      </button>

                      {isOpen ? (
                        <ul className="border-t border-border bg-surface-sunken/40 px-4 py-2">
                          {rows.slice(0, 50).map((m) => (
                            <li key={m.id}>
                              <Link
                                to={`/moulds/${encodeURIComponent(m.assetCode)}/edit`}
                                className="flex items-center justify-between gap-3 rounded-md px-2 py-1.5 text-sm hover:bg-surface"
                              >
                                <span className="flex min-w-0 items-center gap-2">
                                  <Code className="font-semibold">{m.assetCode}</Code>
                                  <span className="truncate text-muted-foreground">{m.name}</span>
                                </span>
                                <span className="shrink-0 text-xs font-medium text-primary">Fix</span>
                              </Link>
                            </li>
                          ))}
                          {rows.length > 50 ? (
                            <li className="px-2 py-1.5 text-xs text-muted-foreground">
                              …and {rows.length - 50} more
                            </li>
                          ) : null}
                        </ul>
                      ) : null}
                    </li>
                  );
                })}
              </ul>
            </Card>
          );
        })}
      </div>

      <div className="mt-4 flex justify-end">
        <Button variant="secondary" asChild>
          <Link to="/moulds">Open the catalog</Link>
        </Button>
      </div>
    </Page>
  );
}
